/**
 * Investment calculator ("Immobilienrechner", PLAN.md §3.9 `investment_calculation`), engine v2.
 *
 * A port of Nico Kremer's reference calculator (BRIMO_Rechner_AngerapperPlatz_Mettmann.html, `simulate()`,
 * `ekRendite()` and `fillPrint()`, 10/2026; owner decision 19.10.2026): a monthly 30-year loop with a fixed
 * annuity during the fixed-interest period and a recomputed annuity on the remaining balance with the
 * follow-up rates afterwards, a liquidity rent (target rent, "Soll-Miete") and a tax rent (actual rent,
 * "Kaltmiete (Ist)") that both rise by the rent increase after every full interval, linear depreciation on
 * (purchase price + acquisition costs) × building share that ends after 100 / rate years, and the "seller
 * pays the acquisition costs" mode (Nico's `ohneEK`: price grossed up, everything financed, no equity).
 * Since 30.10.2026 (owner decision „Wir wollen alles aus Nicos Rechner", D-347) also his financing share
 * (`finanzanteil`, loan = price × share, up to 120 %), the manual AfA amount, the „Zusatz-AfA" of years 1–10, the
 * parking-space rent and the switch „Tilgung als Kosten".
 * The arithmetic runs in euros; inputs and results are whole cents (rounded half away from zero) and plain
 * percentages. The "who pays" attribution, the IRR and the sale block of engine v1 are kept on top of the
 * new year figures.
 */
/** Horizon of the year series (Nico's `MAX = 30`). */
export const INVESTMENT_HORIZON_YEARS = 30;
/** Years over which the IRR is calculated (sale at the end of year 10). */
export const INVESTMENT_IRR_YEARS = 10;
/** Holding period after which the sale is tax-free (§ 23 EStG, "Spekulationsfrist"). */
export const TAX_FREE_SALE_AFTER_YEARS = 10;
/** Rent-increase interval of a subsidy cycle ("Subventionszyklen (à 36 Monate)"). */
export const DEFAULT_RENT_INCREASE_INTERVAL_MONTHS = 36;
/** Years in which the „Zusatz-AfA" is deducted (Nico's `y <= 10 ? p.zusatzAfa : 0`). */
export const ADDITIONAL_DEPRECIATION_YEARS = 10;
/** Highest financing share in % of the purchase price („Finanzierungsanteil", Nico's `finanzanteil`). */
export const MAX_FINANCING_SHARE_PCT = 120;
export function calculateInvestment(input) {
    assertInput(input);
    const purchasePrice = toEuros(input.purchasePriceCents);
    const grossedUpPrice = input.sellerPaysAcquisitionCosts
        ? purchasePrice / (1 - input.acquisitionCostsPct / 100)
        : purchasePrice;
    const acquisitionCosts = input.sellerPaysAcquisitionCosts
        ? grossedUpPrice - purchasePrice
        : (purchasePrice * input.acquisitionCostsPct) / 100;
    const renovation = Math.max(0, toEuros(input.renovationCents) - toEuros(input.renovationSubsidyCents));
    const totalInvestment = purchasePrice + acquisitionCosts + renovation;
    // Nico's `mitEK`: fin = price × finanzanteil, ek = max(0, gesamtinvest − fin); `ohneEK` finances everything.
    const loan = input.sellerPaysAcquisitionCosts
        ? totalInvestment
        : input.financingSharePct !== null
            ? (purchasePrice * input.financingSharePct) / 100
            : Math.max(0, totalInvestment - toEuros(input.equityCents));
    const equity = Math.max(0, totalInvestment - loan);
    const depreciationBase = ((purchasePrice + acquisitionCosts) * input.buildingSharePct) / 100;
    const manualDepreciation = input.depreciationManualCents;
    const depreciationFull = manualDepreciation !== null
        ? toEuros(manualDepreciation)
        : (depreciationBase * input.depreciationPct) / 100;
    const depreciationYears = manualDepreciation !== null || input.depreciationPct <= 0 ? Infinity : 100 / input.depreciationPct;
    const additionalDepreciationYear = toEuros(input.additionalDepreciationCents);
    const deductibleYear = toEuros(input.deductibleCostsCents) * 12;
    const nonDeductibleYear = toEuros(input.nonDeductibleCostsCents) * 12;
    const unscheduled = toEuros(input.unscheduledRepaymentCents);
    const interval = input.rentIncreaseIntervalMonths;
    // A subsidy cycle is the rent-increase interval; without rent increases („0 = never") it stays 36 months
    // („Subventionszyklen (à 36 Monate)", D-342).
    const subsidyMonths = input.rentSubsidyCycles * (interval > 0 ? interval : DEFAULT_RENT_INCREASE_INTERVAL_MONTHS);
    const increase = 1 + input.rentIncreasePct / 100;
    const raw = [];
    let balance = loan;
    let rate = input.interestPct / 100 / 12;
    const annuityMonthly = (loan * (input.interestPct + input.repaymentPct)) / 100 / 12;
    let annuity = annuityMonthly;
    const parkingRent = toEuros(input.parkingSpaceRentCents);
    let targetRent = toEuros(input.rentCents) + parkingRent;
    let actualRent = toEuros(input.rentTaxCents) + parkingRent;
    let cumulative = 0;
    let breakEvenYear = null;
    for (let year = 1; year <= INVESTMENT_HORIZON_YEARS; year++) {
        let interest = 0;
        let principal = 0;
        let rent = 0;
        let rentTax = 0;
        let subsidised = false;
        for (let m = 1; m <= 12; m++) {
            const month = (year - 1) * 12 + m;
            if (month > 1 && interval > 0 && (month - 1) % interval === 0) {
                targetRent *= increase;
                actualRent *= increase;
            }
            if (month === input.fixedInterestYears * 12 + 1) {
                rate = input.followUpInterestPct / 100 / 12;
                annuity = (balance * (input.followUpInterestPct + input.followUpRepaymentPct)) / 100 / 12;
            }
            const monthInterest = balance * rate;
            const monthPrincipal = Math.min(balance, Math.max(0, annuity - monthInterest));
            balance -= monthPrincipal;
            interest += monthInterest;
            principal += monthPrincipal;
            rentTax += actualRent;
            if (input.rentSubsidyCycles === 0) {
                rent += targetRent;
            }
            else if (month <= subsidyMonths) {
                rent += toEuros(input.rentCents) + parkingRent;
                subsidised = true;
            }
            else {
                rent += actualRent;
            }
        }
        const extra = Math.min(balance, unscheduled);
        balance -= extra;
        principal += extra;
        const fullYears = Math.floor(depreciationYears);
        const depreciation = year <= fullYears
            ? depreciationFull
            : year === fullYears + 1
                ? depreciationFull * (depreciationYears - fullYears)
                : 0;
        const additionalDepreciation = year <= ADDITIONAL_DEPRECIATION_YEARS ? additionalDepreciationYear : 0;
        const taxable = rentTax - deductibleYear - interest - depreciation - additionalDepreciation;
        const tax = (-taxable * input.taxRatePct) / 100;
        const cashflow = rent - interest - principal - deductibleYear - nonDeductibleYear + tax;
        cumulative += cashflow;
        if (breakEvenYear === null && cashflow >= 0)
            breakEvenYear = year;
        raw.push({
            year,
            rent,
            rentTax,
            deductible: deductibleYear,
            nonDeductible: nonDeductibleYear,
            depreciation,
            additionalDepreciation,
            subsidised,
            interest,
            principal,
            taxable,
            tax,
            cashflow,
            cumulative,
            value: purchasePrice * Math.pow(1 + input.appreciationPct / 100, year),
            debt: balance,
        });
    }
    // IRR by bisection on the 10-year net present value with the equity (value − debt) in year 10.
    const npv = (r) => {
        let sum = -equity;
        for (let i = 0; i < INVESTMENT_IRR_YEARS; i++) {
            const row = rawAt(raw, i + 1);
            const flow = i === INVESTMENT_IRR_YEARS - 1 ? row.cashflow + (row.value - row.debt) : row.cashflow;
            sum += flow / Math.pow(1 + r, i + 1);
        }
        return sum;
    };
    let lo = -0.6;
    let hi = 1.5;
    for (let i = 0; i < 60; i++) {
        const mid = (lo + hi) / 2;
        if (npv(mid) > 0)
            lo = mid;
        else
            hi = mid;
    }
    const irr = (lo + hi) / 2;
    // Who paid the installment and the repayment: cumulative rent, tax effect and own payments; the repayment
    // goes first to the own payments, the rest proportionally to rent and tax effect.
    let cumulativeRent = 0;
    let cumulativeTax = 0;
    // Nico's `cumEigen`: Σ max(0, eigenMonat) × 12 with eigenMonat = −cash / 12, the principal only as a cost.
    let ownPayments = 0;
    // Σ negative cash flows (principal always included) for the attribution of the repayment.
    let cashOwnPayments = 0;
    let equityExceedsOwnPaymentsYear = null;
    let gainExceedsOwnPaymentsYear = null;
    const years = raw.map((row) => {
        cumulativeRent += row.rent;
        cumulativeTax += row.tax;
        const costCashflow = input.repaymentAsCost ? row.cashflow : row.cashflow + row.principal;
        ownPayments += Math.max(0, -costCashflow);
        cashOwnPayments += Math.max(0, -row.cashflow);
        const equityValue = row.value - row.debt;
        const repaid = Math.max(0, loan - row.debt);
        const repaidFromOwnPocket = Math.min(repaid, cashOwnPayments);
        const repaidRest = Math.max(0, repaid - repaidFromOwnPocket);
        const denominator = cumulativeRent + cumulativeTax || 1;
        const netGain = equityValue - ownPayments;
        if (equityExceedsOwnPaymentsYear === null && equityValue >= equity - row.cumulative) {
            equityExceedsOwnPaymentsYear = row.year;
        }
        if (gainExceedsOwnPaymentsYear === null && netGain >= 0)
            gainExceedsOwnPaymentsYear = row.year;
        const installmentMonthly = (row.interest + row.principal + row.deductible + row.nonDeductible) / 12;
        const tenant = Math.min(installmentMonthly, row.rent / 12);
        const taxOffice = Math.max(0, Math.min(installmentMonthly - tenant, row.tax / 12));
        return {
            year: row.year,
            rentCents: toCents(row.rent),
            rentTaxCents: toCents(row.rentTax),
            deductibleCostsCents: toCents(row.deductible),
            nonDeductibleCostsCents: toCents(row.nonDeductible),
            depreciationCents: toCents(row.depreciation),
            additionalDepreciationCents: toCents(row.additionalDepreciation),
            subsidised: row.subsidised,
            interestCents: toCents(row.interest),
            principalCents: toCents(row.principal),
            paymentCents: toCents(row.interest + row.principal),
            taxableResultCents: toCents(row.taxable),
            taxEffectCents: toCents(row.tax),
            cashflowCents: toCents(row.cashflow),
            cumulativeCashflowCents: toCents(row.cumulative),
            propertyValueCents: toCents(row.value),
            remainingDebtCents: toCents(row.debt),
            equityCents: toCents(equityValue),
            repaidCents: toCents(repaid),
            appreciationCents: toCents(row.value - totalInvestment),
            ownPaymentsCents: toCents(ownPayments),
            netGainCents: toCents(netGain),
            repaidFromOwnPocketCents: toCents(repaidFromOwnPocket),
            repaidByTenantCents: toCents((repaidRest * cumulativeRent) / denominator),
            repaidByTaxOfficeCents: toCents((repaidRest * cumulativeTax) / denominator),
            installment: {
                totalCents: toCents(installmentMonthly),
                interestCents: toCents(row.interest / 12),
                principalCents: toCents(row.principal / 12),
                tenantCents: toCents(tenant),
                taxOfficeCents: toCents(taxOffice),
                ownCents: toCents(Math.max(0, installmentMonthly - tenant - taxOffice)),
            },
        };
    });
    const saleRow = rawAt(raw, input.saleYear);
    const cumulativeDepreciation = raw
        .slice(0, input.saleYear)
        .reduce((sum, row) => sum + row.depreciation + row.additionalDepreciation, 0);
    const taxableGain = saleRow.value - (purchasePrice - cumulativeDepreciation);
    const taxFree = input.saleYear >= TAX_FREE_SALE_AFTER_YEARS;
    const saleTax = taxFree ? 0 : (taxableGain * input.taxRatePct) / 100;
    const saleYearOwnPayments = yearAt(years, input.saleYear).ownPaymentsCents;
    const year1 = rawAt(raw, 1);
    const costs1 = year1.deductible + year1.nonDeductible;
    const cashflowBeforeTax1 = year1.rent - year1.interest - year1.principal - costs1;
    const year10 = rawAt(raw, INVESTMENT_IRR_YEARS);
    return {
        acquisitionCostsCents: toCents(acquisitionCosts),
        grossedUpPriceCents: toCents(grossedUpPrice),
        totalInvestmentCents: toCents(totalInvestment),
        equityCents: toCents(equity),
        loanCents: toCents(loan),
        annuityCents: toCents(annuityMonthly * 12),
        annuityMonthlyCents: toCents(annuityMonthly),
        rentYear1Cents: toCents(year1.rent),
        grossYieldPct: totalInvestment > 0 ? (year1.rent / totalInvestment) * 100 : 0,
        nonRecoverableCostsCents: toCents(costs1),
        depreciationBaseCents: toCents(depreciationBase),
        depreciationCents: toCents(depreciationFull),
        repaymentAsCost: input.repaymentAsCost,
        oneOffTaxRefundCents: toCents((toEuros(input.maintenanceExpenseCents) * input.taxRatePct) / 100),
        rentSubsidyMonths: subsidyMonths,
        interestYear1Cents: toCents(year1.interest),
        taxEffectYear1Cents: toCents(year1.tax),
        cashflowBeforeTaxCents: toCents(cashflowBeforeTax1),
        cashflowAfterTaxCents: toCents(year1.cashflow),
        monthly: {
            rentCents: toCents(year1.rent / 12),
            nonRecoverableCostsCents: toCents(costs1 / 12),
            annuityCents: toCents((year1.interest + year1.principal) / 12),
            interestCents: toCents(year1.interest / 12),
            principalCents: toCents(year1.principal / 12),
            taxEffectCents: toCents(year1.tax / 12),
            cashflowBeforeTaxCents: toCents(cashflowBeforeTax1 / 12),
            cashflowAfterTaxCents: toCents(year1.cashflow / 12),
            depreciationCents: toCents(year1.depreciation / 12),
        },
        irrPct: irr * 100,
        breakEvenYear,
        equityExceedsOwnPaymentsYear,
        gainExceedsOwnPaymentsYear,
        wealthGrowth10yCents: toCents(year10.value - year10.debt - equity + year10.cumulative),
        repaid10yCents: toCents(loan - year10.debt),
        appreciation10yCents: toCents(year10.value - purchasePrice),
        propertyValue10yCents: toCents(year10.value),
        remainingDebt10yCents: toCents(year10.debt),
        cumulativeCashflow10yCents: toCents(year10.cumulative),
        years,
        sale: {
            year: input.saleYear,
            proceedsCents: toCents(saleRow.value),
            remainingDebtCents: toCents(saleRow.debt),
            cumulativeDepreciationCents: toCents(cumulativeDepreciation),
            taxableGainCents: toCents(taxableGain),
            taxFree,
            saleTaxCents: toCents(saleTax),
            netProceedsCents: toCents(saleRow.value - saleRow.debt - saleTax),
            gainVsPurchasePriceCents: toCents(saleRow.value - purchasePrice),
            averageOwnPaymentMonthlyCents: Math.round(saleYearOwnPayments / (input.saleYear * 12)),
        },
    };
}
/** The year's figures per month (Nico's "Monatliche Bilanz"). */
export function monthlyBalance(result, year) {
    assertYear(year, 'year');
    const row = yearAt(result.years, year);
    const own = row.interestCents +
        (result.repaymentAsCost ? row.principalCents : 0) +
        row.deductibleCostsCents +
        row.nonDeductibleCostsCents -
        row.rentCents -
        row.taxEffectCents;
    return {
        rentCents: toCents(toEuros(row.rentCents) / 12),
        taxEffectCents: toCents(toEuros(row.taxEffectCents) / 12),
        interestCents: toCents(toEuros(row.interestCents) / 12),
        principalCents: toCents(toEuros(row.principalCents) / 12),
        deductibleCostsCents: toCents(toEuros(row.deductibleCostsCents) / 12),
        nonDeductibleCostsCents: toCents(toEuros(row.nonDeductibleCostsCents) / 12),
        ownShareCents: toCents(toEuros(own) / 12),
    };
}
/** Nico's `ekRendite(S, H, kaufpreis)`: invested capital and return over `horizonYears`. */
export function equityReturn(result, horizonYears, purchasePriceCents) {
    assertYear(horizonYears, 'horizonYears');
    const row = yearAt(result.years, horizonYears);
    const invested = toEuros(result.equityCents) - toEuros(row.cumulativeCashflowCents);
    const withAppreciation = toEuros(row.propertyValueCents) - toEuros(row.remainingDebtCents);
    const withoutAppreciation = toEuros(purchasePriceCents) - toEuros(row.remainingDebtCents);
    const cagr = (out) => invested <= 0 || out <= 0 ? null : (Math.pow(out / invested, 1 / horizonYears) - 1) * 100;
    const total = (out) => (invested > 0 ? ((out - invested) / invested) * 100 : null);
    return {
        investedCents: toCents(invested),
        returnWithoutAppreciationCents: toCents(withoutAppreciation),
        returnWithAppreciationCents: toCents(withAppreciation),
        ratePaWithoutPct: cagr(withoutAppreciation),
        ratePaWithPct: cagr(withAppreciation),
        totalWithoutPct: total(withoutAppreciation),
        totalWithPct: total(withAppreciation),
    };
}
function assertInput(input) {
    assertYear(input.saleYear, 'saleYear');
    assertYear(input.installmentYear, 'installmentYear');
    assertYear(input.fixedInterestYears, 'fixedInterestYears');
    const cents = [
        'purchasePriceCents',
        'renovationCents',
        'renovationSubsidyCents',
        'maintenanceExpenseCents',
        'equityCents',
        'unscheduledRepaymentCents',
        'rentCents',
        'rentTaxCents',
        'parkingSpaceRentCents',
        'rentSubsidyCents',
        'deductibleCostsCents',
        'nonDeductibleCostsCents',
        'additionalDepreciationCents',
    ];
    for (const key of cents) {
        const value = input[key];
        if (!Number.isFinite(value) || value < 0)
            throw new RangeError(`${key} must not be negative, got ${value}`);
    }
    if (!Number.isInteger(input.rentIncreaseIntervalMonths) || input.rentIncreaseIntervalMonths < 0) {
        throw new RangeError(`rentIncreaseIntervalMonths must be a non-negative integer, got ${input.rentIncreaseIntervalMonths}`);
    }
    if (!Number.isInteger(input.rentSubsidyCycles) || input.rentSubsidyCycles < 0) {
        throw new RangeError(`rentSubsidyCycles must be a non-negative integer, got ${input.rentSubsidyCycles}`);
    }
    if (input.depreciationManualCents !== null &&
        (!Number.isFinite(input.depreciationManualCents) || input.depreciationManualCents < 0)) {
        throw new RangeError(`depreciationManualCents must not be negative, got ${input.depreciationManualCents}`);
    }
    if (input.financingSharePct !== null &&
        !(input.financingSharePct >= 0 && input.financingSharePct <= MAX_FINANCING_SHARE_PCT)) {
        throw new RangeError(`financingSharePct must be between 0 and ${MAX_FINANCING_SHARE_PCT}, got ${input.financingSharePct}`);
    }
    if (input.acquisitionCostsPct < 0 || input.acquisitionCostsPct >= 100) {
        throw new RangeError(`acquisitionCostsPct must be between 0 and 100, got ${input.acquisitionCostsPct}`);
    }
}
function assertYear(year, name) {
    if (!Number.isInteger(year) || year < 1 || year > INVESTMENT_HORIZON_YEARS) {
        throw new RangeError(`${name} must be an integer between 1 and ${INVESTMENT_HORIZON_YEARS}, got ${year}`);
    }
}
function rawAt(rows, year) {
    const row = rows[year - 1];
    if (!row)
        throw new RangeError(`no year ${year} in the series`);
    return row;
}
function yearAt(rows, year) {
    const row = rows[year - 1];
    if (!row)
        throw new RangeError(`no year ${year} in the series`);
    return row;
}
function toEuros(cents) {
    return cents / 100;
}
/** Whole cents, rounded half away from zero (never −0). */
function toCents(euros) {
    const cents = Math.round(Math.abs(euros) * 100);
    return cents === 0 ? 0 : euros < 0 ? -cents : cents;
}
//# sourceMappingURL=investment.js.map